/**
 * @fileoverview "Contas com acesso" do modal de usuário (Empresas). As contas
 * escolhidas ficam à vista, cada uma com "Principal" (ou "Tornar principal") e
 * o botão de remover; a busca abre a lista das outras contas por cima do modal:
 * filtra ao digitar (sem ligar para acento), rola por dentro, abre para cima
 * quando falta espaço embaixo e funciona pelo teclado (setas, Enter, Esc).
 *
 * Regras da principal (o salvamento exige uma, entre as escolhidas): a primeira
 * conta adicionada vira a principal; remover a principal promove a próxima.
 */

import React, { useMemo, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { Search, X } from "lucide-react";

const byName = (a, b) => (a.name || "").localeCompare(b.name || "", "pt-BR", { sensitivity: "base" });

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  return {
    searchWrap: { position: "relative" },
    searchIcon: {
      position: "absolute",
      left: 14,
      top: "50%",
      transform: "translateY(-50%)",
      color: dark ? "#71717A" : "#94a3b8",
      pointerEvents: "none",
    },
    // A lista flutuante fica por cima do modal (que está em 1300).
    popper: { zIndex: 1400 },
    // Menu no padrão do select do app: fundo sólido, cantos 14, itens em pílula.
    paper: {
      marginTop: 6,
      marginBottom: 6,
      borderRadius: 14,
      background: dark ? "linear-gradient(135deg, #121213 0%, #18181b 100%)" : "#ffffff",
      border: `1px solid ${dark ? "rgba(91, 124, 255, 0.18)" : "rgba(15, 23, 42, 0.08)"}`,
      boxShadow: dark ? "0 18px 40px rgba(0, 0, 0, 0.4)" : "0 18px 40px rgba(15, 23, 42, 0.12)",
    },
    listbox: { padding: 6, maxHeight: 240 },
    option: {
      borderRadius: 10,
      minHeight: 38,
      fontSize: 13.5,
      color: dark ? "#e5e7eb" : "#0f172a",
      '&[data-focus="true"]': { background: dark ? "rgba(91, 124, 255, 0.16)" : "rgba(61, 92, 253, 0.08)" },
    },
    noOptions: { fontSize: 13, color: dark ? "#71717A" : "#64748b" },

    // Contas escolhidas
    list: {
      marginTop: 8,
      borderRadius: 12,
      border: `1px solid ${dark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.08)"}`,
      overflow: "hidden",
    },
    row: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "6px 6px 6px 14px",
      minHeight: 40,
      borderBottom: `1px solid ${dark ? "rgba(255,255,255,0.05)" : "rgba(15,23,42,0.06)"}`,
      "&:last-child": { borderBottom: "none" },
    },
    name: {
      flex: 1,
      minWidth: 0,
      fontSize: 13.5,
      fontWeight: 500,
      color: dark ? "#FAFAFA" : "#09090B",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    primary: {
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: "0.04em",
      textTransform: "uppercase",
      color: dark ? "#7C9BFF" : "#3D5CFD",
      padding: "0 6px",
    },
    makePrimary: {
      border: "none",
      background: "transparent",
      padding: "4px 6px",
      borderRadius: 6,
      font: "inherit",
      fontSize: 12,
      color: dark ? "#A1A1AA" : "#64748b",
      cursor: "pointer",
      "&:hover": { color: dark ? "#FAFAFA" : "#09090B", background: dark ? "rgba(255,255,255,0.05)" : "rgba(15,23,42,0.05)" },
      "&:focus-visible": { outline: `2px solid ${dark ? "#5B7CFF" : "#3D5CFD"}`, outlineOffset: 1 },
    },
    remove: {
      width: 28,
      height: 28,
      flexShrink: 0,
      border: "none",
      background: "transparent",
      borderRadius: 8,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      color: dark ? "#71717A" : "#94a3b8",
      cursor: "pointer",
      "&:hover": { background: "rgba(239,68,68,0.1)", color: "#EF4444" },
      "&:focus-visible": { outline: `2px solid ${dark ? "#5B7CFF" : "#3D5CFD"}`, outlineOffset: 1 },
    },
    empty: {
      marginTop: 8,
      padding: "12px 14px",
      borderRadius: 12,
      fontSize: 12.5,
      color: dark ? "#71717A" : "#64748b",
      border: `1px dashed ${dark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.12)"}`,
    },
  };
});

/**
 * @param {object} props
 * @param {{id: number, name: string}[]} props.accounts Contas que podem ser escolhidas.
 * @param {number[]} props.selectedIds Contas escolhidas, na ordem em que entraram.
 * @param {number|null} props.primaryId A principal (uma das escolhidas).
 * @param {(ids: number[], primaryId: number|null) => void} props.onChange
 * @param {string} [props.inputClassName] Classe do campo de busca (a dos campos do modal).
 */
const AccountsPicker = ({ accounts, selectedIds, primaryId, onChange, inputClassName }) => {
  const classes = useStyles();
  const [query, setQuery] = useState("");
  const selected = useMemo(
    () => selectedIds.map((id) => accounts.find((a) => a.id === id)).filter(Boolean),
    [accounts, selectedIds]
  );
  const available = useMemo(
    () => accounts.filter((a) => !selectedIds.includes(a.id)).sort(byName),
    [accounts, selectedIds]
  );

  const add = (account) => {
    const ids = [...selectedIds, account.id];
    onChange(ids, ids.includes(primaryId) ? primaryId : account.id);
  };
  const remove = (id) => {
    const ids = selectedIds.filter((x) => x !== id);
    onChange(ids, id === primaryId ? ids[0] ?? null : primaryId);
  };

  return (
    <>
      <Autocomplete
        options={available}
        value={null}
        inputValue={query}
        onInputChange={(e, value, reason) => {
          if (reason !== "reset") setQuery(value);
        }}
        onChange={(e, account) => {
          if (!account) return;
          add(account);
          setQuery("");
        }}
        getOptionLabel={(a) => a.name || ""}
        openOnFocus
        autoHighlight
        disableCloseOnSelect
        clearOnBlur
        noOptionsText={query ? "Nenhuma conta encontrada" : "Todas as contas já foram adicionadas"}
        classes={{
          popper: classes.popper,
          paper: classes.paper,
          listbox: classes.listbox,
          option: classes.option,
          noOptions: classes.noOptions,
        }}
        renderInput={(params) => (
          <div ref={params.InputProps.ref} className={classes.searchWrap}>
            <Search size={16} className={classes.searchIcon} aria-hidden="true" />
            <input
              {...params.inputProps}
              className={inputClassName}
              style={{ paddingLeft: 38 }}
              placeholder="Adicionar conta…"
              aria-label="Adicionar conta"
            />
          </div>
        )}
      />
      {selected.length === 0 ? (
        <div className={classes.empty}>Nenhuma conta adicionada. Busque acima para adicionar.</div>
      ) : (
        <div className={classes.list}>
          {selected.map((account) => (
            <div key={account.id} className={classes.row}>
              <span className={classes.name} title={account.name}>
                {account.name}
              </span>
              {account.id === primaryId ? (
                <span className={classes.primary}>Principal</span>
              ) : (
                <button
                  type="button"
                  className={classes.makePrimary}
                  onClick={() => onChange(selectedIds, account.id)}
                >
                  Tornar principal
                </button>
              )}
              <button
                type="button"
                className={classes.remove}
                onClick={() => remove(account.id)}
                aria-label={`Remover ${account.name}`}
                title="Remover"
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default AccountsPicker;
